import { Timestamp } from "firebase/firestore";
import { z } from "zod";

/**
 * Dinner seatings (TNG-94). The model is described in
 * functions/python/app/application/service/seating_command.py:
 *
 * - events/{e}/seatings/{s}: name and order are the organiser's; status,
 *   score and the published version are the backend's.
 * - .../tables/{t}: the organiser's dinner tables, each with its seats.
 * - .../seats/{attendeeId}: the draft. `pinned` marks a hand placement a
 *   suggestion keeps; pinned with no table is "not at this seating".
 * - .../published/{t}: what attendees read while the seating is published.
 *
 * "Dinner table" throughout, never "table" alone: the attendee pages already
 * call tracks "tables" (TNG-92).
 */

/**
 * DELETING is set by DELETE_SEATING before it removes anything, and stays
 * until the seating document itself is gone. The rules refuse every client
 * write below a seating while it is set, so the organiser board shows it read
 * only rather than as a draft it could not save.
 */
export const SEATING_STATUSES = ["DRAFT", "PUBLISHED", "DELETING"] as const;
export type SeatingStatus = (typeof SEATING_STATUSES)[number];

export const MAX_SEATING_NAME = 100;
export const MAX_TABLE_CAPACITY = 50;
export const DEFAULT_SEATING_NAME = "Dinner";

export const seatingSchema = z.object({
  id: z.string(),
  name: z.string(),
  displayOrder: z.number().optional().catch(undefined),
  status: z.enum(SEATING_STATUSES).catch("DRAFT"),
  createdAt: z.instanceof(Timestamp).nullable().optional().catch(undefined),
  seed: z.number().optional().catch(undefined),
  score: z.number().optional().catch(undefined),
  stats: z
    .object({
      sameCompanyPairs: z.number(),
      sameTrackPairs: z.number(),
      sharedSignalPairs: z.number(),
    })
    .optional()
    .catch(undefined),
  guestCount: z.number().optional().catch(undefined),
  unseatedCount: z.number().optional().catch(undefined),
  publishedVersion: z.number().optional().catch(undefined),
  publishedAt: z.instanceof(Timestamp).nullable().optional().catch(undefined),
  suggestedAt: z.instanceof(Timestamp).nullable().optional().catch(undefined),
});
export type Seating = z.infer<typeof seatingSchema>;

export const seatingTableSchema = z.object({
  id: z.string(),
  name: z.string(),
  displayOrder: z.number().catch(0),
  capacity: z.number().catch(0),
});
export type SeatingTable = z.infer<typeof seatingTableSchema>;

export const seatSchema = z.object({
  id: z.string(),
  tableId: z.string().nullable().catch(null),
  pinned: z.boolean().catch(false),
});
export type Seat = z.infer<typeof seatSchema>;

export const publishedTableSchema = z.object({
  id: z.string(),
  name: z.string().catch(""),
  displayOrder: z.number().catch(0),
  capacity: z.number().catch(0),
  attendeeIds: z.array(z.string()).catch([]),
  version: z.number().optional().catch(undefined),
});
export type PublishedTable = z.infer<typeof publishedTableSchema>;

/** Seatings in the order the organiser set, then oldest first. */
export function sortSeatings<T extends Seating>(seatings: T[]): T[] {
  return [...seatings].sort(
    (a, b) =>
      (a.displayOrder ?? 0) - (b.displayOrder ?? 0) ||
      (a.createdAt?.toMillis() ?? Number.MAX_SAFE_INTEGER) -
        (b.createdAt?.toMillis() ?? Number.MAX_SAFE_INTEGER) ||
      a.id.localeCompare(b.id),
  );
}

/** Dinner tables by their order, then name. */
export function sortTables<T extends { displayOrder: number; name: string }>(
  tables: T[],
): T[] {
  return [...tables].sort(
    (a, b) =>
      a.displayOrder - b.displayOrder ||
      a.name.localeCompare(b.name, undefined, { numeric: true }),
  );
}
